import StockChip from '@javascript/components/application/stockChip';
import VehicleResult from '@javascript/components/vehicle_search/vehicleResult';
import useQueryParam from '@javascript/lib/hooks/useQueryParam';

import type {
  EventType,
  InstantSearchResult,
} from '@sharedTypes/InstantSearch';
import type { Vehicle } from '@sharedTypes/Vehicle';
import useCurrentSortOrder from './hooks/useCurrentSortOrder';
interface InstantSearchVehicleProps {
  hit: InstantSearchResult;
  sendEvent: (
    event: EventType,
    hit: InstantSearchResult,
    label: string
  ) => void;
  analytics?: () => void;
  branchDistances?: Record<string, { distance_to: number; name: string }>;
  clickAndCollectDetails?: Vehicle['clickAndCollectDetails'];
  hideImageSlider?: boolean;
}

const InstantSearchVehicle = ({
  hideImageSlider,
  hit,
  analytics,
  branchDistances,
  clickAndCollectDetails,
  sendEvent,
}: InstantSearchVehicleProps) => {
  const { branchInfo, stockCount, imageCount, titleInfo, make } = hit;
  const [location] = useQueryParam('location');
  const index = useCurrentSortOrder();

  const excludedFranchiseCodes = ['busc', 'busi', 'mota'];
  const MILES_TO_METERS = 1609;

  const isExcludedFranchise = (branchInfo: InstantSearchResult['branchInfo']) =>
    excludedFranchiseCodes.includes(branchInfo.franchiseCodes?.[0] ?? '');

  const shouldIncludeDistance = (
    hit: InstantSearchResult,
    branchInfo: InstantSearchResult['branchInfo']
  ) =>
    hit?._rankingInfo?.geoDistance &&
    branchInfo.dmsid &&
    !isExcludedFranchise(branchInfo);

  const getBranchNameAndUrl = () => {
    if (!location) return {};
    const encodedLocation = encodeURIComponent(location);

    if (!branchInfo.dmsid && !isExcludedFranchise(branchInfo)) {
      return {
        name: `Find ${make} branches near ${location}`,
        url: `/find-a-dealer/search/near-${encodedLocation}?franchise_id=${branchInfo?.franchiseCodes?.[0]}`,
      };
    }
    if (isExcludedFranchise(branchInfo)) {
      return {
        url: `/find-a-dealer/search/near-${encodedLocation}?franchise_id[]=${branchInfo?.franchiseCodes?.[0]}&franchise_id[]=${branchInfo?.franchiseCodes?.[1]}`,
      };
    }
    return {};
  };

  const getDistanceToCustomer = () => {
    if (shouldIncludeDistance(hit, branchInfo)) {
      const geoDistance = hit?._rankingInfo?.geoDistance ?? 0;

      const distanceToCustomer = Math.round(
        index?.includes('geolocation')
          ? geoDistance / MILES_TO_METERS
          : geoDistance
      );

      return {
        distance_to_customer: distanceToCustomer,
      };
    }
    return {};
  };

  const prefix =
    branchDistances && branchDistances[branchInfo.dmsid]?.distance_to > 15
      ? { prefix: 'Move from' }
      : { prefix: 'Click & Collect from', icon: 'collect' };

  const modifiedBranchInfo = {
    ...branchInfo,
    ...getBranchNameAndUrl(),
    ...(clickAndCollectDetails && prefix),
    ...getDistanceToCustomer(),
  };

  const sendEventWithHit = (event: EventType, label: string) =>
    sendEvent(event, hit, label);

  return (
    <VehicleResult
      {...hit}
      algoliaQueryID={hit?.__queryID}
      analytics={analytics}
      branch={modifiedBranchInfo}
      clickAndCollectDetails={clickAndCollectDetails}
      hideImageSlider={hideImageSlider}
      instantSearchAnalytics={sendEventWithHit}
      isMovable={
        !!branchDistances && branchDistances[branchInfo.dmsid]?.distance_to > 15
      }
      title={titleInfo}
    >
      {stockCount !== undefined && (
        <StockChip count={stockCount} imageCount={imageCount} />
      )}
    </VehicleResult>
  );
};

export default InstantSearchVehicle;
